import DateFnsUtils from '@date-io/date-fns';
import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  CircularProgress,
  createStyles,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  makeStyles,
  MenuItem,
  Radio,
  Typography
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { FieldArray, FieldArrayRenderProps, useField, useFormikContext } from 'formik';
import React, { ChangeEventHandler, Fragment, memo, useCallback, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Checkbox, DialogTitle, InputBase, LanguageString, LSTextField, TextField } from '..';
import { OValues, QValues, TypedReflect } from '../../common';
import { tmpId } from '../../contexts';
import { FormOptionType, FormQuestionType, FormRatingQuestionOrientation } from '../../dto';
import { DialogController, useCustomProperties } from '../../hooks';
import { KeyPress } from './common';

type QuestionOptionProps = {
  name: string;
  checked: boolean;
  single: boolean;
  autoFocus?: boolean;
  hasCodes?: boolean;
  onDeleteClick?: () => void;
  onEnterPress?: () => void;
  onChange?: () => void
};

type EditQuestionDialogProps = DialogController & {
  question: QValues;
  fieldName: string;
};

const useStyles = makeStyles(theme =>
  createStyles({
    optionsList: {
      width: '100%'
    }
  })
);

const capitalizeStyle = { textTransform: 'capitalize' as const };
const buttonStyle = { marginTop: 0 };
const containerStyle = { alignItems: 'center', gap: '24px' };
const questionOptionStyle = { paddingTop: 0, paddingBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' } as const;
const codeFieldRegex = /(.*)\.text$/;
const codeFieldReplacement = '$1.code';

const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const QuestionOption: React.FC<QuestionOptionProps> = memo(({ name, checked, single, onDeleteClick, onEnterPress, onChange, autoFocus, hasCodes }) => {
  const handleKeyPress: KeyPress = useCallback((event) => {
    if (onEnterPress != null && event.key === 'Enter') onEnterPress();
  }, [onEnterPress]);

  const correctIndicator = single ? <Radio checked={checked} onChange={onChange} /> : <MuiCheckbox checked={checked} onChange={onChange} />;
  const deleteItem = (
    <IconButton onClick={onDeleteClick}>
      <Icon>delete</Icon>
    </IconButton>
  );

  const codeFieldName = useMemo(() => name.replace(codeFieldRegex, codeFieldReplacement), [name]);

  return (
    <ListItem style={questionOptionStyle}>
      <InputBase name={name} startAdornment={correctIndicator} endAdornment={deleteItem} onKeyPress={handleKeyPress} autoFocus={autoFocus} />
      {hasCodes && <TextField id={`edit_question_${codeFieldName}`} name={codeFieldName} label="Code" /> }
    </ListItem>
  );
});

const QuestionOptionsInner: React.FC<FieldArrayRenderProps> = ({ name, remove, push }) => {
  const formik = useFormikContext<QValues>();
  // TODO: Hack to get the 'Question' object for these options
  const qFieldName = name.substr(0, name.lastIndexOf('.'));
  const [{ value: question }] = useField<QValues>(qFieldName);
  const { options } = question;
  const classes = useStyles();
  const [selectedOption, setSelectedOption] = useState<OValues | undefined>();
  const [hasCodes, setHasCodes] = useState<boolean>(options?.some(opt => opt.code != null) ?? true);

  const answers = useMemo(() => {
    let ans = question.answer ? (JSON.parse(question.answer) as number[]) : [];
    if (!Array.isArray(ans)) ans = [ans];
    return ans;
  }, [question.answer]);

  const isHtmlInputElement = (element: EventTarget | HTMLInputElement): element is HTMLInputElement => 'files' in element;
  const handleAddFromCsv = useCallback(() => {
    const input: HTMLInputElement = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e: Event & { target: EventTarget | HTMLInputElement | null }) => {
      if (e.target == null) return;
      const target = e.target;
      if (!isHtmlInputElement(target)) return;
      const file = target.files?.[0];
      if (file == null) return;

      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target == null) return;
        const csvContent = event.target.result;
        if (typeof csvContent !== 'string') return;

        try {
          const lines = csvContent.trim().split('\n');
          if (lines.length < 2) {
            alert('CSV file must have headers and at least one data row');
            return;
          }

          const headerLine = lines[0];
          const headers = parseCSVLine(headerLine);
          const textIndex = headers.indexOf('text');
          const codeIndex = headers.indexOf('code');

          if (textIndex === -1) {
            alert('CSV must have a "text" column');
            return;
          }

          unstable_batchedUpdates(() => {
            for (let i = 1; i < lines.length; i++) {
              const line = lines[i].trim();
              if (line == null) continue;

              const values = parseCSVLine(line);
              const text = values[textIndex];
              const code = codeIndex !== -1 ? values[codeIndex] : undefined;

              if (text != null) {
                const newOption: OValues = {
                  id: String(tmpId.nextId),
                  text: text
                };

                if (hasCodes && code !== undefined) {
                  newOption.code = code;
                }

                push(newOption);
              }
            }
          });
        } catch (error: unknown) {
          console.error('Error parsing CSV:', error);
          alert('Error parsing CSV file');
        }
      };

      reader.readAsText(file);
    };
    input.click();
  }, [push, hasCodes]);

  const handleAddOption = useCallback(() => {
    const newOption = {
      id: String(tmpId.nextId),
      text: ''
    };

    push(newOption);
    setSelectedOption(newOption);
  }, [push]);

  const updateAnswer = useCallback((index: number) => {
    const newAnswers = [...answers];
    newAnswers.includes(index) ? newAnswers.splice(newAnswers.indexOf(index), 1) : newAnswers.push(index);
    newAnswers.sort((a, b) => a - b);
    formik.setFieldValue(`${qFieldName}.answer`, JSON.stringify(newAnswers));
  }, [answers, qFieldName, formik]);

  const handleCodesChange = useCallback((_, checked: boolean) => setHasCodes(checked), []);

  return (
    <Grid item>
      <List
        className={classes.optionsList}
        subheader={
          <ListSubheader>
            <Grid container style={containerStyle}>
              <Typography>Answers</Typography>
              <FormControlLabel control={<MuiCheckbox onChange={handleCodesChange} />} checked={hasCodes} label="Has Codes" />
              <Box ml="auto" clone>
                <Button style={buttonStyle} onClick={handleAddOption}>Add</Button>
              </Box>
              <Button style={buttonStyle} onClick={handleAddFromCsv}>Add from CSV</Button>
            </Grid>
          </ListSubheader>
        }
        dense
      >
        {options?.map((o, index) => (
          <QuestionOption
            key={o.id}
            name={`${name}[${index}].text`}
            checked={answers.includes(index)}
            single={question.questionType === FormQuestionType.SELECT}
            onChange={() => updateAnswer(index)}
            // TODO: write a 'remove' function that updates answer indexes (if needed) and then calls 'remove'
            onDeleteClick={() => remove(index)}
            onEnterPress={(index === options.length - 1) ? handleAddOption : undefined}
            autoFocus={selectedOption?.id === o.id}
            hasCodes={hasCodes}
          />
        ))}
      </List>
    </Grid>
  );
};

// TODO: Remove this hack when Formik fixes their type definitions - see https://github.com/formium/formik/issues/1736
const QuestionOptions: React.FC<FieldArrayRenderProps | void> = QuestionOptionsInner as React.FC<FieldArrayRenderProps | void>;

const DateAnswer: React.FC<{ name: string }> = ({ name: fieldName }) => {
  const [field] = useField<QValues>(fieldName);

  const handleChange = () => {
    console.log('date change');
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DatePicker fullWidth {...field} label="Answer" onChange={handleChange} />
    </MuiPickersUtilsProvider>
  );
};

const QuestionAnswers: React.FC<{ name: string }> = ({ name: fieldName }) => {
  const [{ value: question }] = useField<QValues>(fieldName);

  switch (question.questionType) {
    case FormQuestionType.TEXT:
      return <TextField id={`${fieldName}.answer`} item name={`${fieldName}.answer`} label="Answer" />;
    case FormQuestionType.MULTISELECT:
    case FormQuestionType.SELECT:
      return question.optionType === FormOptionType.STATIC ? (
        <FieldArray name={`${fieldName}.options`} component={QuestionOptions} />
      ) : (
        <div>Cannot edit dynamic options yet</div>
      );
    case FormQuestionType.DATE:
    case FormQuestionType.DATETIME:
    case FormQuestionType.DUEDATE:
    case FormQuestionType.TIME:
      return (
        <Grid item>
          <DateAnswer name={`${fieldName}.answer`} />
        </Grid>
      );
    default:
      return null;
  }
};

type OptionProps = { fieldName: string };
const TextLengthOptions: React.FC<OptionProps> = memo(({ fieldName }) => (
  <Fragment>
    <TextField id={`df_${fieldName}.minLength`} item name={`${fieldName}.minLength`} label="Min Length" type="number" />
    <TextField id={`df_${fieldName}.maxLength`} item name={`${fieldName}.maxLength`} label="Max Length" type="number" />
    <TextField id={`df_${fieldName}.match`} item name={`${fieldName}.match`} label="Validation Pattern" />
  </Fragment>
));

const FormOptionTypeKeys = TypedReflect.ownKeys(FormOptionType);
const SelectOptionType: React.FC<OptionProps> = memo(({ fieldName }) => (
  <Grid item>
    <TextField id={`df_${fieldName}.optionType`} name={`${fieldName}.optionType`} label="Option Type" select style={capitalizeStyle}>
      {FormOptionTypeKeys.map((key) => (
        <MenuItem key={key} value={key} style={capitalizeStyle}>
          {key.toLowerCase()}
        </MenuItem>
      ))}
    </TextField>
  </Grid>
));

const FormRatingOrientationKeys = TypedReflect.ownKeys(FormRatingQuestionOrientation);
const RatingOptions: React.FC<OptionProps> = memo(({ fieldName }) => (
  <Fragment>
    <TextField id={`df_${fieldName}.minRateLevel`} item name={`${fieldName}.minRateLevel`} type="number" label="min levels" />
    <TextField id={`df_${fieldName}.rateLevels`} item name={`${fieldName}.rateLevels`} type="number" label="levels" />
    <LSTextField id={`df_${fieldName}.lowText`} item name={`${fieldName}.lowText`} label="Low Text" />
    <LSTextField id={`df_${fieldName}.highText`} item name={`${fieldName}.highText`} label="High Text" />
    <TextField id={`df_${fieldName}.orientation`} item name={`${fieldName}.orientation`} label="Orientation" select style={capitalizeStyle}>
      {FormRatingOrientationKeys.map(key => (
        <MenuItem key={key} value={key} style={capitalizeStyle}>
          {key.toLowerCase()}
        </MenuItem>
      ))}
    </TextField>
  </Fragment>
));

const FormQuestionTypeKeys = TypedReflect.ownKeys(FormQuestionType);

// TODO: Remove all except one answer when swithcing from 'multi' to 'single' select.
export const EditQuestionDialog: React.FC<EditQuestionDialogProps> = ({ fieldName, props, controller }) => {
  const formik = useFormikContext<{ [name: string]: string }>();
  const [{ value: qType }] = useField<FormQuestionType>(`${fieldName}.questionType`);
  const { loading, data } = useCustomProperties();

  const handleTypeChange: ChangeEventHandler<HTMLInputElement> = useCallback(() => {
    formik.setFieldValue(`${fieldName}.answer`, undefined);
    if (qType !== FormQuestionType.SELECT && qType !== FormQuestionType.MULTISELECT) {
      formik.setFieldValue(`${fieldName}.options`, undefined);
    }
  }, [qType, fieldName, formik]);

  return loading ? (
    <CircularProgress color="primary" />
  ) : (
    <Dialog {...props} maxWidth="md" fullWidth>
      <DialogTitle onClose={controller.setClose}>Edit Question</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} direction="column">
          <TextField id={`df_${fieldName}.name`} item name={`${fieldName}.name`} label="Name" />
          <LSTextField id={`df_${fieldName}.text`} item name={`${fieldName}.text`} label="Text" />
          <LSTextField
            id={`df_${fieldName}.hint`}
            item
            name={`${fieldName}.hint`}
            label={<LanguageString groupName="FORM_EDITOR" resourceName="QUESTION_HINT" defaultText="Hint" />}
          />
          <TextField id={`df_${fieldName}.questionType`} item name={`${fieldName}.questionType`} label="Type" select style={capitalizeStyle} onChange={handleTypeChange}>
            {FormQuestionTypeKeys.map(key => (
              <MenuItem key={key} value={key} style={capitalizeStyle}>
                {key.toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
            {qType === FormQuestionType.TEXT && <TextField id={`edit_question_${fieldName}.rows`} item name={`${fieldName}.rows`} label="Rows" type="number" />}
          {(qType === FormQuestionType.TEXT || qType === FormQuestionType.PASSWORD) && <TextLengthOptions fieldName={fieldName} />}
          {(qType === FormQuestionType.TEXT || qType === FormQuestionType.PASSWORD) && <Checkbox item name={`${fieldName}.encrypt`} label="Encrypt" />}
          {(qType === FormQuestionType.TEXT || qType === FormQuestionType.PASSWORD) && <Checkbox item name={`${fieldName}.transient`} label="Prevent Storing Answer" />}
          {(qType === FormQuestionType.SELECT || qType === FormQuestionType.MULTISELECT) && <SelectOptionType fieldName={fieldName} />}
          {qType === FormQuestionType.RATING && <RatingOptions fieldName={fieldName} />}
          <TextField id={`edit_question_${fieldName}.userProp`} item name={`${fieldName}.userPropertyId`} label="User Property" select style={{ textTransform: 'capitalize' }}>
            {data?.site.customProperties?.map(cp => (
              <MenuItem key={cp.id} value={cp.id} style={{ textTransform: 'capitalize' }}>
                {cp.name}
              </MenuItem>
            ))}
          </TextField>
          <Grid item>
            <Checkbox name={`${fieldName}.isRequired`} label="Required" />
          </Grid>
          <QuestionAnswers name={fieldName} />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
